import { usePermissions } from "common/core/current_user_role";
import { DocumentRequirementEnum, OrganizationTypeEnum } from "graphql_globals";
import { useFeatureFlag } from "common/feature_gating";
import { SIGN_AHEAD } from "constants/feature_gates";

import type { DocumentsOrganization } from "./organization_fragment.graphql";
import type { Config, SectionConfig as SectionConfigField } from "../../config";
import type { DocumentsTransaction } from "./transaction_fragment.graphql";

export function useDefaultDocumentRequirementSpecificProps({
  placeAnOrderUIEnabled,
  defaultDocRequirement,
  organization,
  transaction,
}: {
  placeAnOrderUIEnabled: Config["placeAnOrderUIEnabled"];
  defaultDocRequirement: Config["defaultDocRequirement"];
  organization: DocumentsOrganization;
  transaction: DocumentsTransaction;
}) {
  const { hasPermissionFor } = usePermissions();
  const { templates, organizationType } = organization;
  // TODO: [BIZ-9616] Confirm correct props are included for REAL_ESTATE_PROOF and REAL_ESTATE_ESIGN
  const canEditDocs = hasPermissionFor("editOrganizationTransactions");
  const signAheadEnabled = useFeatureFlag(SIGN_AHEAD);

  if (transaction.isMortgage) {
    const allowDownload = hasPermissionFor("viewDownloadOriginalPdf");

    return {
      cannotEditDocs: !canEditDocs,
      openAnnotateModalAfterDocumentsUploaded: !placeAnOrderUIEnabled,
      allowDownload,
      canRequireMeeting: signAheadEnabled,
      // we don't support template creation for title agencies right now, but we do allow
      // title agencies to upload templates as docs if they have any (through closing ops)
      disableTemplateUpload: organization.templates.totalCount === 0,
    };
  }

  if (defaultDocRequirement === DocumentRequirementEnum.ESIGN) {
    return {
      openAnnotateModalAfterDocumentsUploaded: true,
      cannotEditDocs: !canEditDocs,
      // we don't support template creation for title/lender right now, but we do allow
      // them to upload templates as docs if they have any (through closing ops)
      // Business orgs can upload templates as docs and create templates, so we don't disable for them
      disableTemplateUpload:
        (organizationType === OrganizationTypeEnum.TITLE_AGENCY ||
          organizationType === OrganizationTypeEnum.LENDER) &&
        templates.totalCount === 0,
    };
  }

  return {
    openAnnotateModalAfterDocumentsUploaded: true,
    cannotEditDocs: !canEditDocs,
    disableTemplateUpload: false,
  };
}

export function modifyDocumentsConfigValue<
  SectionConfig extends Partial<Config>,
  SectionConfiguredField extends Extract<keyof SectionConfig, SectionConfigField>,
>(config: SectionConfig, field: SectionConfiguredField, newValue: boolean) {
  const fieldConfig = config[field]!;
  if (typeof fieldConfig === "object" && "value" in fieldConfig) {
    if (!fieldConfig.locked) {
      config[field] = { ...fieldConfig, value: newValue };
    }
  }
}
